import React, { useEffect, useState } from "react";
import { createStyles, Grid, makeStyles, Typography } from "@material-ui/core";
import "jspdf-autotable";
import { AxiosError } from "axios";
import WithLoading from "../../../../utils/WithLoading";
import { CustomTheme } from "../../../../types/customTheme";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import { buttonColors } from "../../../../utils/enum";
import {
  createAlertConfigInfo,
  deleteAlertConfigInfo,
  fetchAlertConfigInfo,
  updateAlertConfigInfo,
} from "../../../../services/insightApp/metrics";
import { useRouteMatch } from "react-router-dom";
import {
  ERROR_MESSAGE_UNEXPECTED_ERROR,
  SUCCESSFULLY_CREATED,
  SUCCESSFULLY_UPDATED,
} from "../../../../utils/consts";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import CreateNewNode from "./CreateNewNode";
import AlertConfigModal from "./AlertConfigModal";
import CardCommon from "../../../../components/card/CardCommon";
import AlertConfigEditModal from "./AlertConfigEditModal";

type StationInfo = {
  [id: number]: {
    code: string;
    name: string;
  };
};

type Threshold = {
  warningThreshold: number;
  criticalThreshold: number;
};

type StationMetrics = {
  [code: string]: Threshold & {
    name: string;
    code: string;
  };
};

interface AlertConfig {
  preparationAverageTime: Threshold;
  locationAverageTime: Threshold;
  loadAverageTime: Threshold;
  driverAverageTime: Threshold;
  stationAverageTime: any;
}

interface AlertConfigNodeProps {
  isLoading: boolean;
  stationNode: any; // consider typing this
  alertConfigInfo: Partial<AlertConfig>; // safer and clearer
  setAlertConfigInfo: any;
}

const useStyles = makeStyles((theme: CustomTheme) => createStyles({}));

const DEFAULT_THRESHOLDS: AlertConfig = {
  preparationAverageTime: { warningThreshold: 720, criticalThreshold: 720 },
  locationAverageTime: { warningThreshold: 2700, criticalThreshold: 2700 },
  loadAverageTime: { warningThreshold: 300, criticalThreshold: 300 },
  driverAverageTime: { warningThreshold: 720, criticalThreshold: 720 },
  stationAverageTime: {},
};

const AlertConfigNode: React.FC<AlertConfigNodeProps> = ({
  isLoading,
  stationNode,
  alertConfigInfo,
  setAlertConfigInfo,
}) => {
  const classes = useStyles();
  const [alertConfigNode, setAlertConfigNode] =
    useState<AlertConfig>(DEFAULT_THRESHOLDS);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [errorMessage, setErrorMessage] = useState<any>({});
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [isOpenAlertCreationModal, setIsOpenAlertCreationModal] =
    useState(false);
  const [isOpenAlertEditModal, setIsOpenAlertEditModal] = useState(false);
  const [editableKey, setEditableKey] = useState("");
  const [editableValue, setEditableValue] = useState<any>({});
  const [alertName, setAlertName] = useState("");
  const [isLoadingDelete, setIsLoadingDelete] = useState("");

  const match: any = useRouteMatch();
  const locationId = match?.params?.locationId;

  const mergeStationMetrics = (
    input: StationInfo,
    input1: any,
  ): { stationMetrics: StationMetrics } => {
    const result: any = {};
    for (const key in input) {
      const { code, name } = input[key];
      const thresholds = input1?.[code] || {};

      result[code] = {
        warningThreshold: thresholds?.warningThreshold ?? 600,
        criticalThreshold: thresholds?.criticalThreshold ?? 720,
        name,
        code,
      };
    }

    return result;
  };

  useEffect(() => {
    let mergedConfigObject: any = {};

    Object.entries(alertConfigInfo).forEach(([key, value]) => {
      const stationAverageTime = mergeStationMetrics(
        stationNode,
        value?.stationAverageTime || {},
      );
      const mergedConfig: AlertConfig = {
        preparationAverageTime: {
          warningThreshold:
            value?.preparationAverageTime?.warningThreshold ?? 720,
          criticalThreshold:
            value?.preparationAverageTime?.criticalThreshold ?? 720,
        },
        locationAverageTime: {
          warningThreshold:
            value?.locationAverageTime?.warningThreshold ?? 2700,
          criticalThreshold:
            value?.locationAverageTime?.criticalThreshold ?? 2700,
        },
        loadAverageTime: {
          warningThreshold: value?.loadAverageTime?.warningThreshold ?? 300,
          criticalThreshold: value?.loadAverageTime?.criticalThreshold ?? 300,
        },
        driverAverageTime: {
          warningThreshold: value?.driverAverageTime?.warningThreshold ?? 720,
          criticalThreshold: value?.driverAverageTime?.criticalThreshold ?? 720,
        },
        stationAverageTime,
      };

      mergedConfigObject[key] = mergedConfig;
    });

    setAlertConfigNode(mergedConfigObject);
  }, [alertConfigInfo, stationNode]);

  const stripExtraFields = (data: any) => {
    if (!data.stationAverageTime) return data;

    const stripped = Object.entries(data.stationAverageTime).reduce(
      (acc, [key, value]: [string, any]) => {
        acc[key] = {
          warningThreshold: value.warningThreshold,
          criticalThreshold: value.criticalThreshold,
        };
        return acc;
      },
      {} as Record<string, any>,
    );

    return {
      ...data,
      stationAverageTime: stripped,
    };
  };

  const handleUpdate = async (alertConfigInfo: any) => {
    const data = stripExtraFields(alertConfigInfo);
    data["configDetails"] = {
      alertName: alertName,
    };

    try {
      const res = await updateAlertConfigInfo(locationId, data, editableKey);
      getAlertConfigInfo();
    } catch (error) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsLoadingSubmit(false);
    }
  };

  const getAlertConfigInfo = async () => {
    try {
      const res = await fetchAlertConfigInfo(locationId);
      setAlertConfigInfo(res.data.data);
      setSuccess(SUCCESSFULLY_CREATED);
      setIsLoadingSubmit(false);
      setIsOpenAlertCreationModal(false);
      setIsOpenAlertEditModal(false);
      setAlertName("");
    } catch (error) {
      setError("Please try again later.");
    }
  };

  const handleCreate = async (alertConfigInfo: any) => {
    const data = stripExtraFields(alertConfigInfo);
    data["configDetails"] = {
      alertName: alertName,
    };
    try {
      const res = await createAlertConfigInfo(locationId, data);

      getAlertConfigInfo();
    } catch (error) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsLoadingSubmit(false);
    }
  };

  const handleSubmit = (type: any, alertConfigInfo: any) => {
    const errors: any = {};

    const checkThreshold = (
      key: keyof AlertConfig,
      thresholds: Threshold | Record<string, Threshold>,
    ) => {
      const fieldErrors: any = {};

      if (key === "stationAverageTime") {
        for (const [
          stationKey,
          { warningThreshold, criticalThreshold },
        ] of Object.entries(thresholds)) {
          const stationErrors: Partial<Record<keyof Threshold, boolean>> = {};
          if (
            warningThreshold === undefined ||
            warningThreshold === null ||
            warningThreshold === 0
          ) {
            stationErrors.warningThreshold = true;
          }

          if (
            criticalThreshold === undefined ||
            criticalThreshold === null ||
            criticalThreshold === 0
          ) {
            stationErrors.criticalThreshold = true;
          }

          if (Object.keys(stationErrors).length > 0) {
            fieldErrors[stationKey] = stationErrors;
          }
        }

        if (Object.keys(fieldErrors).length > 0) {
          errors[key] = fieldErrors;
        }
      } else {
        const { warningThreshold, criticalThreshold } = thresholds as Threshold;
        const singleErrors: Partial<Record<keyof Threshold, boolean>> = {};

        if (
          warningThreshold === undefined ||
          warningThreshold === null ||
          warningThreshold === 0
        ) {
          singleErrors.warningThreshold = true;
        }

        if (
          criticalThreshold === undefined ||
          criticalThreshold === null ||
          criticalThreshold === 0
        ) {
          singleErrors.criticalThreshold = true;
        }

        if (Object.keys(singleErrors).length > 0) {
          errors[key] = singleErrors;
        }
      }
    };
    checkThreshold(
      "preparationAverageTime",
      alertConfigInfo.preparationAverageTime,
    );
    checkThreshold("locationAverageTime", alertConfigInfo.locationAverageTime);
    checkThreshold("driverAverageTime", alertConfigInfo.driverAverageTime);
    checkThreshold("loadAverageTime", alertConfigInfo.loadAverageTime);
    checkThreshold("stationAverageTime", alertConfigInfo.stationAverageTime);

    if (alertName === "") {
      errors["alertName"] = { alertName: true };
    }

    if (Object.keys(errors).length > 0) {
      console.log(errors); // <-- This is the structure you want
      setErrorMessage(errors);
      return;
    }
    setIsLoadingSubmit(true);
    if (type === "edit") {
      handleUpdate(alertConfigInfo);
    } else {
      handleCreate(alertConfigInfo);
    }
  };

  const handleCreateNode = () => {
    setIsOpenAlertCreationModal(true);
  };

  const handleOpenAlertEditModal = (key: string, value: any) => {
    setIsOpenAlertEditModal(true);
    setEditableKey(key);
    setEditableValue(value);
  };

  const filterInput = (input: any, input1: any) => {
    const output: any = {};

    for (const key in input) {
      if (key !== String(input1)) {
        output[key] = input[key];
      }
    }

    return output;
  };

  const handleRemoveAlertConfig = async (key: string) => {
    setIsLoadingDelete(key);
    try {
      const res = await deleteAlertConfigInfo(locationId, key);
      const filterAlertConfigInfo = filterInput(alertConfigInfo, key);
      setAlertConfigInfo(filterAlertConfigInfo);
      setIsLoadingDelete("");
    } catch (error) {
      const err: any = error as AxiosError;
      setError(err.response.data.message);
      setIsLoadingSubmit(false);
      setIsLoadingDelete("");
    }
  };
  return (
    <>
      <Grid container spacing={2}>
        {Object.values(alertConfigInfo).length > 0 &&
          Object.entries(alertConfigInfo).map(([key, value]: any) => (
            <Grid item xs={6} key={key}>
              <CardCommon backgroundColor={"entity_background"}>
                <div style={{ padding: "20px" }}>
                  <Typography
                    variant="h5"
                    align="left"
                    style={{ marginBottom: "20px" }}
                  >
                    {value.configDetails.alertName}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={8}>
                      <Typography variant="body1" align="left">
                        Location Average Time
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="body1"
                        align="right"
                      >{`${value.locationAverageTime.criticalThreshold} / ${value.locationAverageTime.warningThreshold}`}</Typography>
                    </Grid>

                    <Grid item xs={8}>
                      <Typography variant="body1" align="left">
                        Preparation Average Time
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="body1"
                        align="right"
                      >{`${value.preparationAverageTime.criticalThreshold} / ${value.preparationAverageTime.warningThreshold}`}</Typography>
                    </Grid>

                    <Grid item xs={8}>
                      <Typography variant="body1" align="left">
                        Load Average Time
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="body1"
                        align="right"
                      >{`${value.loadAverageTime.criticalThreshold} / ${value.loadAverageTime.warningThreshold}`}</Typography>
                    </Grid>

                    <Grid item xs={8}>
                      <Typography variant="body1" align="left">
                        Driver Average Time
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="body1"
                        align="right"
                      >{`${value.driverAverageTime.criticalThreshold} / ${value.driverAverageTime.warningThreshold}`}</Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "end",
                    }}
                  >
                    <ButtonCommon
                      variant="contained"
                      style={{ marginRight: "20px", width: "160px" }}
                      color={buttonColors.UPDATE_BUTTON_COLOR}
                      onClick={() => handleOpenAlertEditModal(key, value)}
                    >
                      Edit
                    </ButtonCommon>
                    <ButtonCommon
                      style={{ width: "160px" }}
                      variant="contained"
                      color={buttonColors.DELETE_BUTTON_COLOR}
                      isLoadingPage={isLoadingDelete === key}
                      onClick={() => handleRemoveAlertConfig(key)}
                    >
                      Delete
                    </ButtonCommon>
                  </Grid>
                </div>
              </CardCommon>
            </Grid>
          ))}
        <CreateNewNode handleCreateNode={handleCreateNode} />
        {/* <Grid item xs={12} style={{ display: "flex", justifyContent: "end" }}>
          <ButtonCommon
            style={{ minWidth: "160px" }}
            color={
              Object.values(alertConfigInfo).length > 0
                ? buttonColors.UPDATE_BUTTON_COLOR
                : buttonColors.CREATE_BUTTON_COLOR
            }
            isLoadingPage={isLoadingSubmit}
            onClick={handleSubmit}
          >
            <Typography
              style={{
                color:
                  Object.values(alertConfigInfo).length > 0 ? "black" : "white",
              }}
            >
              {Object.values(alertConfigInfo).length > 0
                ? "Update"
                : "Configure"}
            </Typography>
          </ButtonCommon>
        </Grid> */}
        {/* <Grid item xs={6}>
          <CardBox
            title="Location Average Time"
            averageTimeType="locationAverageTime"
            data={alertConfigNode.locationAverageTime}
            setAlertConfigNode={setAlertConfigNode}
            errorMessage={errorMessage.locationAverageTime}
            setErrorMessage={setErrorMessage}
          />
        </Grid>
        <Grid item xs={6}>
          <CardBox
            title="Preparation Average Time"
            averageTimeType="preparationAverageTime"
            data={alertConfigNode.preparationAverageTime}
            setAlertConfigNode={setAlertConfigNode}
            errorMessage={errorMessage.preparationAverageTime}
            setErrorMessage={setErrorMessage}
          />
        </Grid>
        <Grid item xs={6}>
          <CardBox
            title="Driver Average Time"
            averageTimeType="driverAverageTime"
            data={alertConfigNode.driverAverageTime}
            setAlertConfigNode={setAlertConfigNode}
            errorMessage={errorMessage.driverAverageTime}
            setErrorMessage={setErrorMessage}
          />
        </Grid>
        <Grid item xs={6}>
          <CardBox
            title="Load Average Time"
            averageTimeType={"loadAverageTime"}
            data={alertConfigNode.loadAverageTime}
            setAlertConfigNode={setAlertConfigNode}
            errorMessage={errorMessage.driverAverageTime}
            setErrorMessage={setErrorMessage}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" align="left">
            Station Average Time
          </Typography>
        </Grid>
        {Object.entries(alertConfigNode.stationAverageTime).map(
          ([key, item]: any) => (
            <Grid item xs={6}>
              <CardBox
                title={item.name}
                averageTimeType={"stationAverageTime"}
                data={item}
                setAlertConfigNode={setAlertConfigNode}
                errorMessage={errorMessage.stationAverageTime}
                setErrorMessage={setErrorMessage}
                station={key}
              />
            </Grid>
          ),
        )} */}
      </Grid>
      <DefaultAlert
        open={!!success}
        handleClose={() => setSuccess("")}
        message={success}
        severity="success"
      />
      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
      {isOpenAlertCreationModal && (
        <AlertConfigModal
          isOpen={isOpenAlertCreationModal}
          setIsOpen={setIsOpenAlertCreationModal}
          handleSubmit={handleSubmit}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          alertName={alertName}
          setAlertName={setAlertName}
          stationNode={stationNode}
          isLoading={isLoadingSubmit}
        />
      )}
      {isOpenAlertEditModal && (
        <AlertConfigEditModal
          isOpen={isOpenAlertEditModal}
          setIsOpen={setIsOpenAlertEditModal}
          handleSubmit={handleSubmit}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          alertName={alertName}
          setAlertName={setAlertName}
          editableValue={editableValue}
          isLoading={isLoadingSubmit}
          stationNode={stationNode}
        />
      )}
    </>
  );
};

export default WithLoading(AlertConfigNode);
